import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { CART_MAX_QTY } from 'constants';
import { logAnalyticsEvent } from 'services/logAnalyticsEvent';
import { useAuthenticationContext } from 'contexts';

const CartContext = createContext();

export function CartProvider({ children }) {
    const [cart, setCart] = useState(() => {
        const saved = localStorage.getItem('foodin_cart');
        if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed.items) return parsed;
        }
        return { restaurantId: null, restaurantMeta: {}, items: {}, meta: {} };
    });

    const [checkoutOpen, setCheckoutOpen] = useState(false);

    const { isAuthenticated } = useAuthenticationContext();
    const prevAuthRef = useRef(isAuthenticated);

    // Clear the cart when the user signs out so anonymous carts don't linger.
    // We track the previous auth state to only fire on the transition,
    // not on initial load when isAuthenticated is still resolving.
    useEffect(() => {
        if (prevAuthRef.current && !isAuthenticated) {
            setCart({ restaurantId: null, restaurantMeta: {}, items: {}, meta: {} });
            localStorage.removeItem('foodin_cart');
        }
        prevAuthRef.current = isAuthenticated;
    }, [isAuthenticated]);

    useEffect(() => {
        localStorage.setItem('foodin_cart', JSON.stringify(cart));
    }, [cart]);

    const conflictRef = useRef(false);

    /**
     * addToCart
     * @returns {boolean} true if added, false if restaurant conflict
     */
    const addToCart = useCallback((dealId, restaurantId, dealMeta, restaurantMeta) => {
        conflictRef.current = false;

        setCart((prev) => {
            if (prev.restaurantId && prev.restaurantId !== restaurantId) {
                conflictRef.current = true;
                return prev;
            }

            const currentQty = prev.items[dealId] || 0;
            if (currentQty >= CART_MAX_QTY) return prev;

            return {
                restaurantId,
                restaurantMeta: prev.restaurantId ? prev.restaurantMeta : (restaurantMeta || {}),
                items: { ...prev.items, [dealId]: currentQty + 1 },
                meta: { ...prev.meta, [dealId]: dealMeta },
            };
        });

        if (!conflictRef.current) {
            logAnalyticsEvent("add_to_cart", {
                deal_id: dealId,
                deal_name: dealMeta?.name || null,
                price: dealMeta?.price || null,
                restaurant_id: restaurantId,
                restaurant_name: restaurantMeta?.name || null,
            });
        }

        return !conflictRef.current;
    }, []);

    /**
     * replaceCart — clears existing cart then adds the new item.
     * Used after the user confirms they want to switch restaurants.
     */
    const replaceCart = useCallback((dealId, restaurantId, dealMeta, restaurantMeta) => {
        setCart({
            restaurantId,
            restaurantMeta: restaurantMeta || {},
            items: { [dealId]: 1 },
            meta: { [dealId]: dealMeta },
        });

        logAnalyticsEvent("add_to_cart", {
            deal_id: dealId,
            deal_name: dealMeta?.name || null,
            price: dealMeta?.price || null,
            restaurant_id: restaurantId,
            restaurant_name: restaurantMeta?.name || null,
        });
    }, []);

    const removeFromCart = useCallback((dealId) => {
        setCart((prev) => {
            const currentQty = prev.items[dealId] || 0;
            if (currentQty <= 0) return prev;

            const newItems = { ...prev.items };
            if (currentQty === 1) {
                delete newItems[dealId];
            } else {
                newItems[dealId] = currentQty - 1;
            }

            const newMeta = { ...prev.meta };
            if (!newItems[dealId]) delete newMeta[dealId];

            const hasItemsLeft = Object.keys(newItems).length > 0;
            return {
                restaurantId: hasItemsLeft ? prev.restaurantId : null,
                restaurantMeta: hasItemsLeft ? prev.restaurantMeta : {},
                items: newItems,
                meta: newMeta,
            };
        });
    }, []);

    const clearCart = useCallback(() => {
        setCart({ restaurantId: null, restaurantMeta: {}, items: {}, meta: {} });
        localStorage.removeItem('foodin_cart');
    }, []);

    const getItemCount = useCallback((dealId) => {
        return cart.items[dealId] || 0;
    }, [cart.items]);

    const openCheckout = useCallback(() => setCheckoutOpen(true), []);
    const closeCheckout = useCallback(() => setCheckoutOpen(false), []);

    const value = useMemo(() => ({
        cart,
        addToCart,
        replaceCart,
        removeFromCart,
        clearCart,
        getItemCount,
        checkoutOpen,
        openCheckout,
        closeCheckout,
    }), [cart, addToCart, replaceCart, removeFromCart, clearCart, getItemCount, checkoutOpen, openCheckout, closeCheckout]);

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
}

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
